import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/GlobalStyles';
import { mobile, touchButton } from '../styles/MobileUtils';

// Task Icon Component
const TaskIcon = () => (
  <svg viewBox="0 0 256 202.65" fill="currentColor" style={{ width: '16px', height: '16px', flexShrink: 0 }}>
    <circle cx="21.39" cy="21.39" r="21.39"/>
    <circle cx="21.39" cy="101.33" r="21.39"/>
    <circle cx="21.39" cy="181.26" r="21.39"/>
    <rect x="76.97" y="166.27" width="179.03" height="29.99" rx="14.99" ry="14.99"/>
    <rect x="76.97" y="86.33" width="179.03" height="29.99" rx="14.99" ry="14.99"/>
    <rect x="76.97" y="6.4" width="179.03" height="29.99" rx="14.99" ry="14.99"/>
  </svg>
);

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="m18.5 2.5 3 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

interface MobilePlannerProps {
  plannerData: any;
  onTaskToggle: (date: string, taskType: 'chore' | 'lunchbox' | 'dinner') => void;
  onTimeSlotToggle: (task: any, date: string, taskIndex: number) => void; // New handler for timeSlots
  onTaskEdit: (task: any) => void;
  onAddTask: () => void; // New handler for adding tasks
  onDateNavigate: (direction: 'prev' | 'next') => void;
  currentDate: Date;
  selectedMembers?: Set<string>; // Add member filter support
}

const MobileContainer = styled.div`
  height: calc(100vh - 80px); /* Account for main app header */
  width: 100%;
  background: ${theme.mobile.gradientBg};
  position: relative;
  overflow: hidden; /* Prevent any scrolling */
  display: flex;
  flex-direction: column;
  
  ${mobile(`
    height: calc(100vh - 60px);
    padding-top: ${theme.mobile.safeAreaTop};
  `)}
`;

const CalendarBackground = styled.div`
  flex: 1;
  width: 100%;
  position: relative;
  overflow: hidden; /* Prevent any scrolling */
  display: flex;
  flex-direction: column;
  min-height: 0;
  height: calc(100% - 25vh); /* Use remaining space minus sliding panel */
  max-height: calc(100% - 25vh);
  background: ${theme.colors.accent}; /* Purple theme background */
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: ${theme.spacing.md} ${theme.spacing.md} ${theme.spacing.sm};
  position: relative;
  z-index: 10;
  flex-shrink: 0;
  box-sizing: border-box;
  
  ${mobile(`
    padding: ${theme.spacing.sm} ${theme.spacing.sm} ${theme.spacing.xs};
  `)}
`;

const MonthTitle = styled.h2`
  color: white;
  font-size: 1.4rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: -0.01em;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  
  ${mobile(`
    font-size: 1.2rem;
  `)}
`;

const NavButton = styled.button`
  ${touchButton}
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: 12px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.2rem;
  font-weight: 600;
  transition: all 0.3s ease;
  border: 1px solid rgba(255, 255, 255, 0.3);
  backdrop-filter: blur(10px);
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    transform: translateY(-1px);
  }
  
  &:active {
    transform: scale(0.96) translateY(0);
    background: rgba(255, 255, 255, 0.25);
  }
`;

const FullCalendarContainer = styled.div`
  flex: 1;
  width: 100%;
  padding: ${theme.spacing.sm} ${theme.spacing.md} ${theme.spacing.md};
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 1;
  min-height: 0;
  overflow: hidden; /* Prevent all scrolling */
  box-sizing: border-box;
  
  ${mobile(`
    padding: ${theme.spacing.xs} ${theme.spacing.sm} ${theme.spacing.sm};
  `)}
`;

const CalendarGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  grid-template-rows: auto repeat(5, minmax(0, 1fr));
  gap: 4px;
  flex: 1;
  width: 100%;
  min-width: 100%;
  min-height: 0;
  height: 100%;
  overflow: hidden; /* Prevent grid from scrolling */
  box-sizing: border-box;
  
  ${mobile(`
    gap: 3px;
    grid-template-columns: repeat(7, minmax(0, 1fr));
  `)}
`;

const DayHeader = styled.div`
  text-align: center;
  padding: ${theme.spacing.xs} 0;
  font-size: 0.8rem;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.8);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  text-shadow: 0 1px 4px rgba(0, 0, 0, 0.3);
  height: auto;
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
  
  ${mobile(`
    font-size: 0.75rem;
    padding: 4px 0;
  `)}
`;

const DayCell = styled.div<{ isToday?: boolean; hasEvents?: boolean; isCurrentMonth?: boolean; isSelected?: boolean }>`
  aspect-ratio: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: ${props => props.isToday ? '700' : '600'};
  color: ${props => {
    if (props.isToday) return theme.colors.deepDark;
    if (!props.isCurrentMonth) return 'rgba(255, 255, 255, 0.4)';
    return 'white';
  }};
  background: ${props => {
    if (props.isToday) return 'rgba(255, 255, 255, 0.95)';
    if (props.isSelected) return 'rgba(255, 255, 255, 0.3)';
    if (props.hasEvents) return 'rgba(255, 255, 255, 0.15)';
    return 'transparent';
  }};
  border: ${props => props.isSelected && !props.isToday ? '2px solid rgba(255, 255, 255, 0.8)' : props.hasEvents && !props.isToday ? '1px solid rgba(255, 255, 255, 0.3)' : 'none'};
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  text-shadow: ${props => props.isToday ? 'none' : '0 1px 3px rgba(0, 0, 0, 0.3)'};
  min-width: 0;
  width: 100%;
  box-sizing: border-box;
  
  ${props => props.isToday && `
    box-shadow: 0 4px 12px rgba(255, 255, 255, 0.3);
  `}
  
  ${props => props.hasEvents && !props.isToday && `
    &::after {
      content: '';
      position: absolute;
      bottom: 3px;
      left: 50%;
      transform: translateX(-50%);
      width: 4px;
      height: 4px;
      background: rgba(255, 255, 255, 0.6);
      border-radius: 50%;
    }
  `}
  
  &:hover {
    background: ${props => {
      if (props.isToday) return 'rgba(255, 255, 255, 0.95)';
      return 'rgba(255, 255, 255, 0.2)';
    }};
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
  }
  
  &:active {
    transform: scale(0.96) translateY(-1px);
  }
  
  ${mobile(`
    border-radius: 10px;
    font-size: 0.9rem;
  `)}
`;

const SlidingPanel = styled.div<{ isExpanded: boolean; isDragging: boolean; dragOffset: number }>`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  background: white;
  border-top-left-radius: 24px;
  border-top-right-radius: 24px;
  transition: ${props => 
    props.isDragging 
      ? 'none' 
      : 'transform 0.4s cubic-bezier(0.25, 0.8, 0.25, 1), box-shadow 0.4s ease'
  };
  z-index: 20;
  box-shadow: 0 -8px 32px rgba(0, 0, 0, 0.1);
  touch-action: none; /* Prevent default touch behaviors */
  will-change: transform; /* Optimize for animations */
  
  height: ${props => props.isExpanded ? '75vh' : '25vh'};
  transform: translateY(${props => {
    const baseTransform = props.isExpanded ? '0' : 'calc(100% - 25vh)';
    if (props.isDragging && props.dragOffset !== 0) {
      if (props.isExpanded) {
        // When expanded, only allow dragging down (positive offset)
        const clampedOffset = Math.max(0, props.dragOffset);
        return `calc(${baseTransform} + ${clampedOffset}px)`;
      } else {
        // When collapsed, only allow dragging up (negative offset)
        const clampedOffset = Math.min(0, props.dragOffset);
        return `calc(${baseTransform} + ${clampedOffset}px)`;
      }
    }
    return baseTransform;
  }});
  
  ${mobile(`
    border-top-left-radius: 20px;
    border-top-right-radius: 20px;
  `)}
`;

const PanelHandle = styled.div`
  width: 40px;
  height: 4px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 2px;
  margin: 12px auto 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(0, 0, 0, 0.3);
    transform: scaleY(1.5);
  }
`;

const PanelContent = styled.div<{ isExpanded: boolean }>`
  padding: 0 ${theme.spacing.lg} ${theme.spacing.xl};
  height: calc(100% - 32px);
  overflow-y: auto;
  
  ${mobile(`
    padding: 0 ${theme.spacing.md} calc(${theme.mobile.bottomNavHeight} + ${theme.spacing.md});
  `)}
`;

const SectionTitle = styled.h3`
  color: ${theme.colors.deepDark};
  font-size: 1.2rem;
  font-weight: 700;
  margin-bottom: ${theme.spacing.lg};
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  letter-spacing: -0.01em;
  
  ${mobile(`
    font-size: 1.1rem;
    margin-bottom: ${theme.spacing.md};
  `)}
`;

const TaskList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    left: calc(${theme.spacing.md} + -1.5px); /* Gap + half of circle width (30px/2 = 15px) */
    top: 24px;
    bottom: 24px;
    width: 2px;
    background: transparent;
    border-left: 2px dotted ${theme.colors.lightText}40;
    border-radius: 1px;
  }
`;

const TimelineItem = styled.div<{ color?: string; completed?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.md};
  padding: ${theme.spacing.md} 0;
  position: relative;
  opacity: ${props => props.completed ? 0.6 : 1};
  
  &:first-child {
    padding-top: 0;
  }
  
  &:last-child {
    padding-bottom: 0;
  }
`;

const TaskDot = styled.button<{ color?: string; completed?: boolean }>`
  width: 30px !important;
  height: 30px !important;
  min-width: 30px;
  min-height: 30px;
  max-width: 30px;
  max-height: 30px;
  border-radius: 50%;
  background: ${props => props.completed ? (props.color || theme.colors.primary) : 'white'};
  border: 2px solid ${props => props.color || theme.colors.primary};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  flex-shrink: 0;
  margin-top: 0;
  position: relative;
  z-index: 2;
  cursor: pointer;
  transition: all 0.2s ease;
  padding: 0 !important;
  box-sizing: border-box;
  
  &:hover {
    transform: scale(1.2);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }
  
  &:active {
    transform: scale(0.85);
  }
  
  ${props => props.completed && `
    &::after {
      content: '✓';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      color: white;
      font-size: 6px;
      font-weight: bold;
      line-height: 1;
    }
  `}
`;

const TaskContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const TaskTitle = styled.div<{ completed?: boolean }>`
  font-weight: 600;
  font-size: 0.85rem;
  color: ${theme.colors.deepDark};
  margin-bottom: 2px;
  line-height: 1.3;
  text-decoration: ${props => props.completed ? 'line-through' : 'none'};
`;

const TaskAssignee = styled.div`
  font-size: 0.7rem;
  color: ${theme.colors.lightText};
  margin-bottom: 4px;
`;

const TaskTime = styled.div`
  font-size: 0.7rem;
  color: ${theme.colors.lightText};
  margin-top: 4px;
  font-weight: 500;
`;

const TaskActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  margin-left: auto;
`;

const ActionButton = styled.button`
  ${touchButton}
  background: ${theme.colors.background};
  border: 1px solid ${theme.colors.lightText}30;
  border-radius: 8px;
  color: ${theme.colors.text};
  font-size: 0.75rem;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  padding: 0;
  
  svg {
    color: ${theme.colors.text};
    opacity: 0.7;
  }
  
  &:hover {
    background: ${theme.colors.lightText}10;
    transform: translateY(-1px);
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    
    svg {
      opacity: 1;
    }
  }
  
  &:active {
    transform: scale(0.95) translateY(0);
    background: ${theme.colors.lightText}20;
  }
`;

const FloatingAddButton = styled.button<{ isVisible: boolean }>`
  ${touchButton}
  position: fixed;
  bottom: calc(${theme.mobile.bottomNavHeight} + ${theme.spacing.lg});
  left: 50%;
  transform: translateX(-50%) ${props => props.isVisible ? 'translateY(0)' : 'translateY(150%)'};
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${theme.colors.accent}, ${theme.colors.darkAccent});
  border: none;
  color: white;
  font-size: 2rem;
  font-weight: 300;
  line-height: 1;
  box-shadow: 
    0 16px 32px ${theme.colors.accent}40,
    0 8px 16px rgba(255, 255, 255, 0.1) inset;
  border: 2px solid rgba(255, 255, 255, 0.2);
  z-index: 50;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  opacity: ${props => props.isVisible ? 1 : 0};
  display: flex;
  align-items: center;
  justify-content: center;
  
  &::before {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    right: 2px;
    bottom: 2px;
    border-radius: 50%;
    background: linear-gradient(135deg, transparent, rgba(255, 255, 255, 0.1));
    pointer-events: none;
  }
  
  &:hover {
    transform: translateX(-50%) translateY(-3px);
    box-shadow: 
      0 20px 40px ${theme.colors.accent}50,
      0 8px 16px rgba(255, 255, 255, 0.15) inset;
  }
  
  &:active {
    transform: translateX(-50%) scale(0.96) translateY(-1px);
  }
  
  ${mobile(`
    width: 56px;
    height: 56px;
    font-size: 1.7rem;
  `)}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${theme.spacing.xl} ${theme.spacing.md};
  color: ${theme.colors.lightText};
`;

const MobilePlanner: React.FC<MobilePlannerProps> = ({
  plannerData,
  onTaskToggle,
  onTimeSlotToggle,
  onTaskEdit,
  onAddTask,
  onDateNavigate,
  currentDate,
  selectedMembers = new Set() // Default to empty set if not provided
}) => {
  const [isPanelExpanded, setIsPanelExpanded] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [dragStartY, setDragStartY] = useState(0);
  const [dragCurrentY, setDragCurrentY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [panelOffset, setPanelOffset] = useState(0);
  const panelRef = useRef<HTMLDivElement>(null);
  const lastUpdateTime = useRef<number>(0);

  // Sync selectedDate with currentDate when navigating months, but keep user selection within the same month
  useEffect(() => {
    // If the selected date is not in the currently viewed month, reset to today or first day of current month
    if (selectedDate.getMonth() !== currentDate.getMonth() || selectedDate.getFullYear() !== currentDate.getFullYear()) {
      const today = new Date();
      // If today is in the current month, select today, otherwise select the first day of the current month
      if (today.getMonth() === currentDate.getMonth() && today.getFullYear() === currentDate.getFullYear()) {
        setSelectedDate(today);
      } else {
        setSelectedDate(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1));
      }
    }
  }, [currentDate, selectedDate]);

  const formatMonth = (date: Date) => {
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add a few days from previous month (only if needed)
    const prevMonth = new Date(year, month - 1, 0);
    const prevMonthDays = Math.min(startDayOfWeek, 3); // Show max 3 days from prev month
    for (let i = prevMonthDays - 1; i >= 0; i--) {
      days.push(new Date(year, month - 1, prevMonth.getDate() - i));
    }
    
    // Add all days of current month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    // Add a few days from next month to complete the grid (max 35 total days = 5 weeks)
    const totalDays = Math.min(35, days.length + (7 - (days.length % 7)));
    let nextMonthDay = 1;
    while (days.length < totalDays) {
      days.push(new Date(year, month + 1, nextMonthDay));
      nextMonthDay++;
    }
    
    return days;
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isCurrentMonth = (date: Date, referenceDate: Date) => {
    return date.getMonth() === referenceDate.getMonth();
  };

  const isSelected = (date: Date) => {
    return date.toDateString() === selectedDate.toDateString();
  };

  const hasEvents = (date: Date) => {
    const dateStr = date.toISOString().split('T')[0];
    return plannerData?.days?.some((day: any) => 
      day.date === dateStr && (day.chore || day.lunchbox || day.dinner || day.timeSlots?.length)
    );
  };

  const getSelectedDateTasks = () => {
    const selectedDateStr = selectedDate.toISOString().split('T')[0];
    const selectedDateData = plannerData?.days?.find((day: any) => day.date === selectedDateStr);
    
    console.log('Getting tasks for selected date:', selectedDateStr);
    console.log('Available days in planner data:', plannerData?.days?.map((d: any) => d.date));
    console.log('Found selected date data:', selectedDateData);
    
    if (!selectedDateData) return [];
    
    // Helper function to check if task should be shown based on member filter (same as web planner)
    const shouldShowTask = (assignedTo: string | undefined) => {
      // If no members are selected, show all tasks
      if (selectedMembers.size === 0) return true;
      
      // If task is not assigned to anyone, show it only if "Unassigned" is selected
      if (!assignedTo) {
        return selectedMembers.has('Unassigned');
      }
      
      // Show task if the assigned member is selected
      return selectedMembers.has(assignedTo);
    };
    
    const tasks: any[] = [];
    
    // Only process timeSlots (main tasks from web planner) - matches web view exactly
    if (selectedDateData.timeSlots) {
      selectedDateData.timeSlots.forEach((slot: any, index: number) => {
        // Apply the same filtering logic as web planner
        if (!shouldShowTask(slot.assignedTo)) return;
        
        tasks.push({
          id: `slot-${index}`,
          title: slot.activity,
          time: slot.startTime || slot.time,
          endTime: slot.endTime,
          type: slot.type,
          color: slot.assignedColor || theme.colors.primary,
          completed: slot.isCompleted,
          assignedTo: slot.assignedTo
        });
      });
    }
    
    // Sort tasks by time
    return tasks.sort((a, b) => {
      const timeA = a.time === 'Morning' ? '06:00' : a.time === 'Before School' ? '07:00' : a.time === 'Evening' ? '18:00' : a.time;
      const timeB = b.time === 'Morning' ? '06:00' : b.time === 'Before School' ? '07:00' : b.time === 'Evening' ? '18:00' : b.time;
      return timeA.localeCompare(timeB);
    });
  };

  const handlePanelToggle = () => {
    setIsPanelExpanded(!isPanelExpanded);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    const touch = e.touches[0];
    setDragStartY(touch.clientY);
    setDragCurrentY(touch.clientY);
    setIsDragging(true);
    
    // Reset any existing offset when starting a new drag
    setPanelOffset(0);
    
    // Prevent any text selection or other default behaviors
    e.preventDefault();
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    
    const touch = e.touches[0];
    setDragCurrentY(touch.clientY);
    
    // Calculate drag offset with simplified, smoother logic
    const dragDistance = touch.clientY - dragStartY;
    let constrainedOffset = dragDistance;
    
    // Apply simple resistance at boundaries
    const maxDrag = 150; // Maximum drag distance
    const resistance = 0.4; // Resistance factor
    
    if (isPanelExpanded) {
      // When expanded, allow dragging down freely, resist dragging up
      if (dragDistance < 0) {
        constrainedOffset = dragDistance * resistance;
      } else {
        constrainedOffset = Math.min(dragDistance, maxDrag);
      }
    } else {
      // When collapsed, allow dragging up freely, resist dragging down
      if (dragDistance > 0) {
        constrainedOffset = dragDistance * resistance;
      } else {
        constrainedOffset = Math.max(dragDistance, -maxDrag);
      }
    }
    
    // Throttle updates for better performance (max 60fps)
    const now = Date.now();
    if (now - lastUpdateTime.current > 16) { // ~60fps
      lastUpdateTime.current = now;
      requestAnimationFrame(() => {
        setPanelOffset(constrainedOffset);
      });
    }
    
    // Prevent default scrolling
    e.preventDefault();
  };

  const handleTouchEnd = () => {
    if (!isDragging) return;
    
    setIsDragging(false);
    
    const dragDistance = dragCurrentY - dragStartY;
    const threshold = 60; // Minimum drag distance to trigger state change
    
    if (Math.abs(dragDistance) > threshold) {
      if (dragDistance > 0) {
        // Dragged down - collapse panel
        setIsPanelExpanded(false);
      } else {
        // Dragged up - expand panel
        setIsPanelExpanded(true);
      }
    }
    // If drag distance is small, snap back to current state (no change)
    
    // Reset drag states immediately for smooth transition
    setPanelOffset(0);
    setDragStartY(0);
    setDragCurrentY(0);
  };

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const days = getDaysInMonth(currentDate);
  const selectedDateTasks = getSelectedDateTasks();

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date);
    setIsPanelExpanded(true); // Auto-expand panel when a date is selected
  };

  const formatSelectedDate = () => {
    const today = new Date();
    if (selectedDate.toDateString() === today.toDateString()) {
      return "Today's Tasks";
    }
    return selectedDate.toLocaleDateString('en-US', { 
      weekday: 'long', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return (
    <MobileContainer>
      <CalendarBackground>
        <CalendarHeader>
          <NavButton onClick={() => onDateNavigate('prev')}>
            ‹
          </NavButton>
          <MonthTitle>{formatMonth(currentDate)}</MonthTitle>
          <NavButton onClick={() => onDateNavigate('next')}>
            ›
          </NavButton>
        </CalendarHeader>
        
        <FullCalendarContainer>
          <CalendarGrid>
            {dayNames.map(day => (
              <DayHeader key={day}>{day}</DayHeader>
            ))}
            {days.map((day, index) => (
              <DayCell
                key={index}
                isToday={isToday(day)}
                hasEvents={hasEvents(day)}
                isCurrentMonth={isCurrentMonth(day, currentDate)}
                isSelected={isSelected(day)}
                onClick={() => handleDateSelect(day)}
              >
                {day.getDate()}
              </DayCell>
            ))}
          </CalendarGrid>
        </FullCalendarContainer>
      </CalendarBackground>
      
      <SlidingPanel 
        ref={panelRef} 
        isExpanded={isPanelExpanded}
        isDragging={isDragging}
        dragOffset={panelOffset}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <PanelHandle onClick={handlePanelToggle} />
        <PanelContent isExpanded={isPanelExpanded}>
          <SectionTitle>
            <TaskIcon /> {formatSelectedDate()}
          </SectionTitle>
          
          {selectedDateTasks.length > 0 ? (
            <TaskList>
              {selectedDateTasks.map((task: any) => (
                <TimelineItem
                  key={task.id}
                  color={task.color}
                  completed={task.completed}
                >
                  <TaskDot 
                    color={task.color} 
                    completed={task.completed}
                    onClick={() => {
                      const dateStr = selectedDate.toISOString().split('T')[0];
                      const taskIndex = parseInt(task.id.replace('slot-', ''));
                      onTimeSlotToggle(task, dateStr, taskIndex);
                    }}
                  />
                  
                  <TaskContent>
                    <TaskTitle completed={task.completed}>
                      {task.title}
                    </TaskTitle>
                    {task.assignedTo && (
                      <TaskAssignee>Assigned to: {task.assignedTo}</TaskAssignee>
                    )}
                    <TaskTime>
                      {task.endTime ? `${task.time} - ${task.endTime}` : task.time}
                    </TaskTime>
                  </TaskContent>
                  
                  <TaskActions>
                    <ActionButton
                      onClick={() => onTaskEdit(task)}
                    >
                      <EditIcon />
                    </ActionButton>
                  </TaskActions>
                </TimelineItem>
              ))}
            </TaskList>
          ) : (
            <EmptyState>
              No tasks for today
            </EmptyState>
          )}
        </PanelContent>
      </SlidingPanel>
      
      <FloatingAddButton isVisible={isPanelExpanded} onClick={onAddTask}>
        +
      </FloatingAddButton>
    </MobileContainer>
  );
};

export default MobilePlanner; 